import { openComposeWithViewState, prepareComposeViewState } from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import { saveWithAction } from '../actions/trySaveMessage';
import upgradeCompose from '../actions/upgradeCompose';
import datapoints from '../datapoints';
import { AttachmentFileAttributes } from 'owa-attachment-file-types';
import ComposeTarget from '../store/schema/ComposeTarget';
import ComposeViewState, { ComposeOperation } from '../store/schema/ComposeViewState';
import { ComposeViewStateInitProps } from '../utils/createComposeViewState';
import { createReplyFromReplyItem, createReplyItem } from '../utils/createReply';
import findInlineComposeViewState from '../utils/findInlineComposeViewState';
import { logReplyInfo } from '../utils/logReplyInfo';
import { ClientItemId, MailboxType } from 'owa-client-ids';
import { getGuid } from 'owa-guid';
import { userMailInteractionAction } from 'owa-mail-actions';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ActionSource, getStore, InstrumentationContext } from 'owa-mail-store';
import { ClientItem } from 'owa-mail-store';
import { isDeepLink } from 'owa-popout';
import Message from 'owa-service/lib/contract/Message';
import { isSxSDisplayed } from 'owa-sxs-store';
import isTabViewEnabled from 'owa-tab-store/lib/utils/isTabViewEnabled';
import { action } from 'satcheljs/lib/legacy';
import { lazyShouldAddinsForceFullCompose } from 'owa-addins-core';

export interface ReplyToMessageCommonOptions {
    isReplyAll: boolean;
    useFullCompose: boolean;
    actionSource: ActionSource;
    fullComposeTarget?: ComposeTarget;
    attachmentFiles?: AttachmentFileAttributes[];
    conversationId?: string;
    instrumentationContexts: InstrumentationContext[];
    initialComposeLogTraceId?: string;
    body?: string;
    groupId?: string;
}

export interface ReplyToMessageAndCreateViewStateOptions extends ReplyToMessageCommonOptions {
    referenceItemOrId: string | Message;
}

export interface ReplyToMessageActionOptions extends ReplyToMessageCommonOptions {
    referenceItemId: string;
    onComposeViewStateCreated?: (newComposeViewState: ComposeViewState) => void;
}

// Remove groupId from ReplyToMessageCommonOptions and add replyToMessage-specific properties
export type ReplyToMessageOptions = Pick<
    ReplyToMessageCommonOptions,
    Exclude<keyof ReplyToMessageCommonOptions, 'groupId'>
> & {
    referenceItemId: ClientItemId;
    onComposeViewStateCreated?: (newComposeViewState: ComposeViewState) => void;
};

export async function replyToMessageAndCreateViewState(
    options: ReplyToMessageAndCreateViewStateOptions
): Promise<ComposeViewState> {
    let viewState: ComposeViewState = null;
    let fullComposeTarget = options.fullComposeTarget;
    const {
        referenceItemOrId,
        isReplyAll,
        useFullCompose,
        conversationId,
        initialComposeLogTraceId,
        groupId,
        body,
        instrumentationContexts,
        attachmentFiles,
        actionSource,
    } = options;

    const tabViewEnabled = isTabViewEnabled();

    if (fullComposeTarget === undefined) {
        fullComposeTarget = isSxSDisplayed()
            ? ComposeTarget.SxS
            : isDeepLink() || !tabViewEnabled
            ? ComposeTarget.PrimaryReadingPane
            : ComposeTarget.SecondaryTab;
    }

    if (!useFullCompose || !tabViewEnabled) {
        const existingViewState = findInlineComposeViewState(conversationId);
        if (existingViewState) {
            await trySaveAndCloseCompose(existingViewState);

            // If there is still default compose viewstate, it means it is failed to save and close previous compose
            // So no-op here
            if (findInlineComposeViewState(conversationId)) {
                return Promise.resolve(null);
            }
        }
    }

    // If traceId is not passed create new one so we can correlate between all replies and sent
    const composeTraceId = initialComposeLogTraceId || getGuid();

    let referenceItem: Message;

    if (typeof referenceItemOrId === 'string') {
        const mailStore = getStore();
        const items = mailStore && mailStore.items;

        if (items && items.has(referenceItemOrId)) {
            referenceItem = items.get(referenceItemOrId) as Message;
        }
    } else {
        referenceItem = referenceItemOrId as Message;
    }

    if (referenceItem) {
        // create a reply item so we can initialize composeInitProps from it
        // before creating the reply
        const replyItem = await createReplyItem(referenceItem, isReplyAll);
        const composeOperation = isReplyAll ? ComposeOperation.ReplyAll : ComposeOperation.Reply;
        if (conversationId && !useFullCompose) {
            const composeInitProps: ComposeViewStateInitProps = {
                operation: composeOperation,
                bodyType: replyItem.NewBodyContent.BodyType,
                subject: replyItem.Subject,
                newContent: body,
                to: replyItem.ToRecipients,
                cc: replyItem.CcRecipients,
                referenceItemId: referenceItem.ItemId,
                referenceItemDocumentId: replyItem.ReferenceItemDocumentId,
                conversationId: conversationId,
                preferAsyncSend: true,
                IRMData: referenceItem.RightsManagementLicenseData,
                logTraceId: composeTraceId,
                hasAttachmentSuggestions: !!(referenceItem as ClientItem).AttachmentSuggestions,
            };

            viewState = await prepareComposeViewState(composeInitProps, groupId, attachmentFiles);
            await openComposeWithViewState(viewState, ComposeTarget.PrimaryReadingPane);

            if (!groupId) {
                // Create the draft in background to prepare for upConvert
                saveWithAction(
                    viewState,
                    createReplyFromReplyItem(replyItem, null).then(message => {
                        if (message) {
                            viewState.itemId = message.ItemId;
                            viewState.subject = message.Subject;
                            viewState.quotedBody = message.Body.Value;
                        }
                    })
                );
            }
        } else {
            const composeInitProps: ComposeViewStateInitProps = {
                operation: composeOperation,
                bodyType: replyItem.NewBodyContent.BodyType,
                subject: replyItem.Subject,
                newContent: body,
                to: replyItem.ToRecipients,
                cc: replyItem.CcRecipients,
                bcc: replyItem.BccRecipients,
                referenceItemId: referenceItem.ItemId,
                preferAsyncSend: true,
                IRMData: referenceItem.RightsManagementLicenseData,
                useSmartResponse: true, // Set useSmartResponse to true in case we can't get quoted body
                logTraceId: composeTraceId,
                isPopout: isDeepLink(),
            };

            if (!isFeatureEnabled('cmp-upgradeCompose')) {
                const message = await createReplyFromReplyItem(replyItem, groupId);
                if (message) {
                    composeInitProps.itemId = message.ItemId;
                    composeInitProps.quotedBody = message.Body.Value;
                    composeInitProps.attachments = message.Attachments;
                    composeInitProps.useSmartResponse = false; // Quoted body is loaded, no need to use smart response now
                }
            }

            viewState = await prepareComposeViewState(composeInitProps, groupId, attachmentFiles);
            await openComposeWithViewState(viewState, fullComposeTarget);

            if (isFeatureEnabled('cmp-upgradeCompose')) {
                upgradeCompose(viewState, createReplyFromReplyItem(replyItem, groupId));
            }
        }

        logReplyInfo(replyItem, composeTraceId, actionSource, referenceItem.DateTimeReceived);

        isReplyAll
            ? userMailInteractionAction('ReplyAll', instrumentationContexts)
            : userMailInteractionAction('Reply', instrumentationContexts);
    }

    return viewState;
}

// We need to have two entrance for reply and replyAll since they are using different datapoint name
const replyToMessageAction = action('replyToMessage', datapoints.MailComposeReply)(
    async function replyToMessageAction(options: ReplyToMessageActionOptions) {
        const composeViewState: ComposeViewState = await replyToMessageAndCreateViewState({
            ...(options as ReplyToMessageCommonOptions),
            referenceItemOrId: options.referenceItemId,
            isReplyAll: false,
        });

        if (options.onComposeViewStateCreated) {
            options.onComposeViewStateCreated(composeViewState);
        }
    }
);

const replyAllToMessageAction = action('replyAllToMessage', datapoints.MailComposeReplyAll)(
    async function replyAllToMessageAction(options: ReplyToMessageActionOptions) {
        const composeViewState: ComposeViewState = await replyToMessageAndCreateViewState({
            ...(options as ReplyToMessageCommonOptions),
            referenceItemOrId: options.referenceItemId,
            isReplyAll: true,
        });

        if (options.onComposeViewStateCreated) {
            options.onComposeViewStateCreated(composeViewState);
        }
    }
);

export default async function replyToMessage(options: ReplyToMessageOptions): Promise<void> {
    const mailboxInfo = options.referenceItemId.mailboxInfo;
    const groupId =
        mailboxInfo && mailboxInfo.type == MailboxType.GroupMailbox ? mailboxInfo.mailboxId : null;

    if (isFeatureEnabled('rp-addins')) {
        const shouldAddinsForceFullCompose = await lazyShouldAddinsForceFullCompose.import();
        if (
            (options.fullComposeTarget === ComposeTarget.PrimaryReadingPane ||
                options.fullComposeTarget === undefined) &&
            shouldAddinsForceFullCompose()
        ) {
            options.useFullCompose = true;
            options.fullComposeTarget = ComposeTarget.SecondaryTab;
        }
    }

    const parameters = {
        ...(options as ReplyToMessageCommonOptions),
        referenceItemId: options.referenceItemId.Id,
        groupId: groupId,
    };

    if (options.isReplyAll) {
        return await replyAllToMessageAction(parameters);
    } else {
        return await replyToMessageAction(parameters);
    }
}
